import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Shield, Map } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { LoadingSpinner } from '../ui/loading-states';
import { useCreateLocation, useUpdateLocation } from '../../hooks/locations/useLocations';
import { useGeolocation } from '../../hooks/locations/useGeolocation';
import LazyLocationMap from './LazyLocationMap';
import type { Location, CreateLocationDto } from '../../types/location.types';

interface LocationFormProps {
  location: Location | null;
  isOpen: boolean;
  onClose: () => void;
}

export const LocationForm: React.FC<LocationFormProps> = ({
  location,
  isOpen,
  onClose,
}) => {
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();

  const [formData, setFormData] = useState<CreateLocationDto>({
    name: '',
    latitude: 0,
    longitude: 0,
    radius_meters: 50,
    address: '',
    description: '',
    requires_gps_validation: false,
    max_gps_deviation_meters: 25,
    is_active: true,
  });


  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [tempCoordinates, setTempCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);

  const { getCurrentPosition, loading: geoLoading, error: geoError, permissionDenied } = useGeolocation();

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        radius_meters: location.radius_meters,
        address: location.address || '',
        description: location.description || '',
        requires_gps_validation: location.requires_gps_validation,
        max_gps_deviation_meters: location.max_gps_deviation_meters || 25,
        is_active: location.is_active,
      });
    }
  }, [location]);

  // Инициализируем временные координаты при открытии модального окна карты
  useEffect(() => {
    if (isMapModalOpen) {
      setTempCoordinates({
        latitude: formData.latitude,
        longitude: formData.longitude,
      });
    } else {
      setTempCoordinates(null);
    }
  }, [isMapModalOpen, formData.latitude, formData.longitude]);

  const handleGetCurrentLocation = async () => {
    try {
      const position = await getCurrentPosition();
      setFormData((prev) => ({
        ...prev,
        latitude: position.latitude,
        longitude: position.longitude,
      }));
      // Очищаем ошибки, если были
      if (errors.location) {
        setErrors((prev) => ({ ...prev, location: '' }));
      }

      // Показываем информационное сообщение, если использовались fallback координаты
      if (position.accuracy >= 1000) {
        console.info('Использованы координаты по умолчанию для Алматы');
      }
    } catch (error) {
      // Эта ошибка не должна возникать, так как мы всегда возвращаем fallback
      console.error('Unexpected geolocation error:', error);
      setErrors({ location: geoError || 'Не удалось получить местоположение' });
    }
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setTempCoordinates({ latitude: lat, longitude: lng });
  };

  const handleConfirmLocationSelection = () => {
    if (tempCoordinates) {
      setFormData((prev) => ({
        ...prev,
        latitude: tempCoordinates.latitude,
        longitude: tempCoordinates.longitude,
      }));
      setTempCoordinates(null);
      setIsMapModalOpen(false);
    }
  };

  const handleCancelLocationSelection = () => {
    setTempCoordinates(null);
    setIsMapModalOpen(false);
  };

  const handleRadiusChange = (newRadius: number) => {
    setFormData((prev) => ({
      ...prev,
      radius_meters: newRadius,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Название обязательно';
    }

    if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Широта должна быть от -90 до 90';
    }

    if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Долгота должна быть от -180 до 180';
    }

    if (formData.radius_meters < 10 || formData.radius_meters > 1000) {
      newErrors.radius_meters = 'Радиус должен быть от 10 до 1000 метров';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      if (location) {
        await updateMutation.mutateAsync({ id: location.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {location ? 'Редактировать локацию' : 'Новая локация'}
          </DialogTitle>
          <DialogDescription>
            {location
              ? 'Измените параметры локации и настройки контроля'
              : 'Создайте новую точку контроля посещаемости'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Основные данные */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Название *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Главный офис"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="address">Адрес</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="ул. Примерная, 123"
              />
            </div>

            <div>
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Дополнительная информация о локации"
                rows={3}
              />
            </div>
          </div>

          {/* Координаты */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <Label>Координаты</Label>
            </div>
            
            {/* Кнопки выбора координат */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsMapModalOpen(true)}
                className="flex-1 sm:flex-none"
              >
                <Map className="h-3 w-3 mr-2" />
                Выбрать на карте
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGetCurrentLocation}
                disabled={geoLoading}
                className="flex-1 sm:flex-none"
              >
                {geoLoading ? (
                  <LoadingSpinner className="h-3 w-3 mr-2" />
                ) : (
                  <Navigation className="h-3 w-3 mr-2" />
                )}
                Мое местоположение
              </Button>
            </div>

            {/* Информация об ошибке геолокации */}
            {geoError && (
              <div className={`rounded-lg p-3 ${
                permissionDenied
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                <p className={`text-sm ${permissionDenied ? 'text-red-800' : 'text-amber-800'}`}>
                  <strong>{permissionDenied ? '🚫' : 'ℹ️'} {permissionDenied ? 'Доступ запрещен:' : 'Примечание:'}</strong> {geoError}
                </p>
                {permissionDenied ? (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-red-700 font-medium">Как разрешить доступ к геолокации:</p>
                    <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5 ml-1">
                      <li>Нажмите на иконку замка/информации в адресной строке браузера</li>
                      <li>Найдите настройки "Местоположение" и выберите "Разрешить"</li>
                      <li>Обновите страницу и попробуйте снова</li>
                    </ul>
                    <p className="text-xs text-red-700 mt-2">
                      Или используйте кнопку "Выбрать на карте" для указания координат.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-amber-700 mt-1">
                    Вы можете вручную указать координаты или выбрать точку на карте.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Широта *</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="0.000001"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                  className={errors.latitude ? 'border-red-500' : ''}
                />
                {errors.latitude && (
                  <p className="text-sm text-red-500 mt-1">{errors.latitude}</p>
                )}
              </div>

              <div>
                <Label htmlFor="longitude">Долгота *</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="0.000001"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                  className={errors.longitude ? 'border-red-500' : ''}
                />
                {errors.longitude && (
                  <p className="text-sm text-red-500 mt-1">{errors.longitude}</p>
                )}
              </div>
            </div>
          </div>

          {/* Настройки контроля */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-500" />
              <Label>Настройки контроля</Label>
            </div>

            <div>
              <Label htmlFor="radius">Радиус действия (метры) *</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="radius"
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={formData.radius_meters}
                  onChange={(e) => setFormData({ ...formData, radius_meters: parseInt(e.target.value) })}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16 text-right">
                  {formData.radius_meters} м
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="gps-validation">Требовать GPS проверку</Label>
              <Switch
                id="gps-validation"
                checked={formData.requires_gps_validation ?? false}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requires_gps_validation: checked })
                }
              />
            </div>

            {formData.requires_gps_validation && (
              <div>
                <Label htmlFor="max-deviation">Погрешность GPS (метры)</Label>
                <Input
                  id="max-deviation"
                  type="number"
                  min="5"
                  max="1000"
                  value={formData.max_gps_deviation_meters}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_gps_deviation_meters: parseInt(e.target.value),
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Добавляется к радиусу действия. Итого: {formData.radius_meters + (formData.max_gps_deviation_meters || 25)} м
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="is-active">Активна</Label>
              <Switch
                id="is-active"
                checked={formData.is_active ?? true}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
              />
            </div>
          </div>
          </form>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={isLoading} onClick={handleSubmit}>
            {isLoading && <LoadingSpinner className="mr-2 h-4 w-4" />}
            {location ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Модальное окно с картой */}
      <Dialog open={isMapModalOpen} onOpenChange={handleCancelLocationSelection}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader>
            <DialogTitle>Выбор локации на карте</DialogTitle>
            <DialogDescription>
              Кликните на карте, чтобы выбрать точку для новой локации, затем нажмите "Выбрать" для подтверждения
            </DialogDescription>
          </DialogHeader>


          {/* Карта */}
          <div className="flex-1 min-h-0 px-6">
            <div className="h-[500px] w-full rounded-lg overflow-hidden border">
              <LazyLocationMap
                latitude={tempCoordinates?.latitude ?? (formData.latitude || 43.238949)}
                longitude={tempCoordinates?.longitude ?? (formData.longitude || 76.889709)}
                radius={formData.radius_meters}
                isInteractive={true}
                onLocationSelect={handleMapLocationSelect}
                onRadiusChange={handleRadiusChange}
                height="100%"
              />
            </div>
          </div>

          {/* Информация о выбранных координатах */}
          {tempCoordinates && (
            <div className="flex-shrink-0 px-6 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-800">
                  <strong>Выбраны координаты:</strong>
                </div>
                <div className="text-sm text-blue-700">
                  Широта: {tempCoordinates.latitude.toFixed(6)}, Долгота: {tempCoordinates.longitude.toFixed(6)}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancelLocationSelection}>
              Отмена
            </Button>
            <Button 
              type="button" 
              onClick={handleConfirmLocationSelection}
              disabled={!tempCoordinates}
            >
              Выбрать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};